import React, { useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Spin } from 'antd';
import styled from 'styled-components';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
`;

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * AuthGuard - protects routes from unauthorized access
 * If user is not authenticated, redirects to Keycloak login
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isInitialized, isAuthenticated, login } = useAuth();

  useEffect(() => {
    // Wait for Keycloak to initialize
    if (!isInitialized) {
      console.log('[AuthGuard] Waiting for Keycloak initialization...');
      return;
    }

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      console.log('[AuthGuard] User not authenticated, redirecting to login...');
      login();
    }
  }, [isInitialized, isAuthenticated, login]);

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Initializing authentication..." />
      </LoadingContainer>
    );
  }

  // Show loading while redirecting to login
  if (!isAuthenticated) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Redirecting to login..." />
      </LoadingContainer>
    );
  }

  // User is authenticated, show protected content
  return <>{children}</>;
};

